import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PhoneProvider } from './contexts/PhoneContext';
import { WebRTCPhone } from './components/phone/WebRTCPhone';
import { ThemeProvider } from './contexts/ThemeContext';
import { RingingPopup } from './components/RingingPopup';
import MidCallPlaybookSidebar from './components/MidCallPlaybookSidebar';
import { Toaster } from './components/ui/sonner';
import LlmBudgetBanner from './components/LlmBudgetBanner';
import ImpersonationBanner from './components/ImpersonationBanner';

// Pages
import LoginPage from './pages/LoginPage';
import ModeSelectorPage from './pages/ModeSelectorPage';
import Dashboard from './pages/Dashboard';
import CallsListPage from './pages/CallsListPage';
import CallDetailPage from './pages/CallDetailPage';
import CallVoiceFabricPage from './pages/CallVoiceFabricPage';
import KnowledgeBasesPage from './pages/KnowledgeBasesPage';
import SettingsPage from './pages/SettingsPage';
import Layout from './components/custom/Layout';
import AchievementsPage from './pages/AchievementsPage';

// Redesigned dashboards (Priority 5-8)
import MyPerformancePage from './pages/my/MyPerformancePage';
import ManagerOpsDashboardPage from './pages/manager/ManagerOpsDashboardPage';
import TeamUsersPage from './pages/TeamUsersPage';
import SmsInboxPage from './pages/SmsInboxPage';
import GymSalesSyncPage from './pages/GymSalesSyncPage';
import DiscInsightsPage from './pages/DiscInsightsPage';
import CommandCenterPage from './pages/CommandCenterPage';
import AgentProfilePage from './pages/manager/AgentProfilePage';
import LiveChatPage from './pages/LiveChatPage';
import ClubSummaryPage from './pages/ClubSummaryPage';
import DemoShowcasePage from './pages/DemoShowcasePage';
import DemoLaunchPage from './pages/DemoLaunchPage';
import HomePage from './pages/HomePage';
import HelpHubPage from './pages/help/HelpHubPage';
import TrainingHubPage from './pages/training/TrainingHubPage';

// Demo login (industry selector)
import DemoLoginPage from './pages/DemoLoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SupervisorTodayPage from './pages/SupervisorTodayPage';

// Portal (white-label domain login)
import PortalLoginPage from './pages/PortalLoginPage';
import TenantIntegrationsPage from './pages/portal/TenantIntegrationsPage';
import TenantBrandingPage from './pages/portal/TenantBrandingPage';

// Admin pages
import AdminOverviewPage from './pages/admin/AdminOverviewPage';
import BulkAttributionReviewPage from './pages/admin/BulkAttributionReviewPage';
import AdminDomainsPage from './pages/admin/AdminDomainsPage';
import AdminDomainDetailPage from './pages/admin/AdminDomainDetailPage';
import AdminDomainPortalPage from './pages/admin/AdminDomainPortalPage';
import AdminSyncLogPage from './pages/admin/AdminSyncLogPage';
import AdminSmokeballSettingsPage from './pages/admin/AdminSmokeballSettingsPage';
import AdminKnowledgeBasesPage from './pages/admin/AdminKnowledgeBasesPage';
import AdminKBSiteConfigPage from './pages/admin/AdminKBSiteConfigPage';
import AdminSiteScopePage from './pages/admin/AdminSiteScopePage';
import AdminScopeLeaksPage from './pages/admin/AdminScopeLeaksPage';
import AdminEmailStatePage from './pages/admin/AdminEmailStatePage';
import ManagerCoachingRecordsPage from './pages/manager/ManagerCoachingRecordsPage';
import DomainSetupPage from './pages/admin/DomainSetupPage';
import CallMasterPage from './pages/admin/CallMasterPage';
import VoiceFabricPage from './pages/admin/VoiceFabricPage';
import FlowCanvasPage from './pages/admin/voicefabric/flow/FlowCanvasPage';
import DomainFlowPage from './pages/admin/voicefabric/flow/DomainFlowPage';
import AdminActivityPage from './pages/admin/AdminActivityPage';
import AdminSystemMigrationsPage from './pages/admin/AdminSystemMigrationsPage';
import AdminAuditIntakesPage from './pages/admin/AdminAuditIntakesPage';
import AdminPhraseOverridesPage from './pages/admin/AdminPhraseOverridesPage';
import AdminTfvfV1_2CalibrationPage from './pages/admin/AdminTfvfV1_2CalibrationPage';
import AdminArchetypePlaybooksPage from './pages/admin/AdminArchetypePlaybooksPage';
import AdminYabbitWebhookPage from './pages/admin/AdminYabbitWebhookPage';
import AdminGymSalesPage from './pages/admin/AdminGymSalesPage';
import AdminYabbitSmsPage from './pages/admin/AdminYabbitSmsPage';
import AdminBrandingPage from './pages/admin/AdminBrandingPage';
import AdminAiAssistantPage from './pages/admin/AdminAiAssistantPage';
import AdminOnboardingPage from './pages/admin/AdminOnboardingPage';
import AdminWastedWarmLeadsPage from './pages/admin/AdminWastedWarmLeadsPage';
import AdminLlmRoutingPage from './pages/admin/AdminLlmRoutingPage';
import AdminDemoLeadsPage from './pages/admin/AdminDemoLeadsPage';
import AdminQueueWatchdogPage from './pages/admin/AdminQueueWatchdogPage';
import AdminVconPage from './pages/admin/AdminVconPage';
import AdminWeeklyReportsPage from './pages/admin/AdminWeeklyReportsPage';
import AdminCostStatementsPage from './pages/admin/AdminCostStatementsPage';
import AdminRebaselineReviewPage from './pages/admin/AdminRebaselineReviewPage';
import AdminYvfCalibrationPage from './pages/admin/AdminYvfCalibrationPage';
import AdminComplianceScorecardPage from './pages/admin/AdminComplianceScorecardPage';
import AdminLlmProviderPage from './pages/admin/AdminLlmProviderPage';
import ImpersonationLogPage from './pages/admin/ImpersonationLogPage';
import PricingPage from './pages/PricingPage';
import VsGongPage from './pages/VsGongPage';
import VsDialpadPage from './pages/VsDialpadPage';
import VsAircallPage from './pages/VsAircallPage';
import VsRingCXPage from './pages/VsRingCXPage';
import VsCallGuardianPage from './pages/VsCallGuardianPage';
import WhyVoiceFabricPage from './pages/WhyVoiceFabricPage';
import FreeAuditPage from './pages/FreeAuditPage';

import '@/App.css';

const Loading = () => (
  <div className="flex items-center justify-center min-h-screen bg-[#0A0F1C]">
    <div className="w-10 h-10 rounded-full border-2 border-teal-500/30 border-t-teal-500 animate-spin" />
  </div>
);

// Route guard: must be logged in
const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Route guard: master_admin only
const AdminOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'master_admin') return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return <Loading />;

  const defaultRedirect = () => {
    if (!user) return '/login';
    if (user.role === 'master_admin') return '/select-mode';
    // Supervisors/managers land on the Command Center (Darren, Jun 2026).
    const MANAGERS = new Set(['domain_admin', 'domain_supervisor', 'office_manager', 'manager', 'supervisor']);
    if (user.domain_id) return MANAGERS.has(user.role) ? '/command-center' : '/my-domain';
    return '/dashboard';
  };

  return (
    <PhoneProvider>
      {user && <RingingPopup />}
      {user && <MidCallPlaybookSidebar />}
      {user && <WebRTCPhone />}
      <Routes>
      {/* Public */}
      <Route path="/login" element={user ? <Navigate to={defaultRedirect()} replace /> : <LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/demo" element={<DemoShowcasePage />} />
      <Route path="/demo/launch" element={<DemoLaunchPage />} />
      <Route path="/demo/:industry" element={<DemoShowcasePage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/vs-gong" element={<VsGongPage />} />
      <Route path="/vs-dialpad" element={<VsDialpadPage />} />
      <Route path="/vs-aircall" element={<VsAircallPage />} />
      <Route path="/vs-ringcx" element={<VsRingCXPage />} />
      <Route path="/vs-call-guardian" element={<VsCallGuardianPage />} />
      <Route path="/why-voicefabric" element={<WhyVoiceFabricPage />} />
      <Route path="/free-audit" element={<FreeAuditPage />} />
      <Route path="/demo" element={<DemoLoginPage />} />
      <Route path="/portal/:slug" element={<PortalLoginPage />} />

      {/* Master admin mode selector */}
      <Route path="/select-mode" element={
        <AdminOnly><ModeSelectorPage /></AdminOnly>
      } />

      {/* Admin panel */}
      <Route path="/admin" element={<AdminOnly><AdminOverviewPage /></AdminOnly>} />
      <Route path="/admin/attribution-review" element={<AdminOnly><Layout><BulkAttributionReviewPage /></Layout></AdminOnly>} />
      <Route path="/admin/domains" element={<AdminOnly><AdminDomainsPage /></AdminOnly>} />
      <Route path="/admin/branding" element={<AdminOnly><AdminBrandingPage /></AdminOnly>} />
      <Route path="/admin/ai-assistant" element={<AdminOnly><AdminAiAssistantPage /></AdminOnly>} />
      <Route path="/admin/domains/new" element={<AdminOnly><DomainSetupPage /></AdminOnly>} />
      <Route path="/admin/domains/:domainId" element={<AdminOnly><AdminDomainDetailPage /></AdminOnly>} />
      <Route path="/admin/domains/:domainId/edit" element={<AdminOnly><DomainSetupPage /></AdminOnly>} />
      <Route path="/admin/domains/:domainId/portal" element={<AdminOnly><AdminDomainPortalPage /></AdminOnly>} />
      <Route path="/admin/domains/:domainId/sync-log" element={<AdminOnly><AdminSyncLogPage /></AdminOnly>} />
      <Route path="/admin/domains/:domainId/smokeball" element={<AdminOnly><AdminSmokeballSettingsPage /></AdminOnly>} />
      <Route path="/admin/call-master" element={<AdminOnly><CallMasterPage /></AdminOnly>} />
      <Route path="/admin/knowledge-bases" element={<AdminOnly><AdminKnowledgeBasesPage /></AdminOnly>} />
      <Route path="/admin/voicefabric" element={<AdminOnly><VoiceFabricPage /></AdminOnly>} />
      <Route path="/admin/voicefabric/flow/:domainId" element={<AdminOnly><DomainFlowPage /></AdminOnly>} />
      <Route path="/admin/voicefabric/flow/:domainId/:ruleId" element={<AdminOnly><FlowCanvasPage /></AdminOnly>} />
      <Route path="/admin/activity" element={<AdminOnly><AdminActivityPage /></AdminOnly>} />
      <Route path="/admin/system-migrations" element={<AdminOnly><AdminSystemMigrationsPage /></AdminOnly>} />
      <Route path="/admin/audit-intakes" element={<AdminOnly><AdminAuditIntakesPage /></AdminOnly>} />
      <Route path="/admin/phrase-overrides" element={<AdminOnly><AdminPhraseOverridesPage /></AdminOnly>} />
      <Route path="/admin/tfvf-v1-2-calibration" element={<AdminOnly><AdminTfvfV1_2CalibrationPage /></AdminOnly>} />
      <Route path="/admin/archetype-playbooks" element={<AdminOnly><AdminArchetypePlaybooksPage /></AdminOnly>} />
      <Route path="/admin/yabbit-webhook" element={<AdminOnly><AdminYabbitWebhookPage /></AdminOnly>} />
      <Route path="/admin/gymsales" element={<AdminOnly><AdminGymSalesPage /></AdminOnly>} />
      <Route path="/admin/yabbit-sms" element={<AdminOnly><AdminYabbitSmsPage /></AdminOnly>} />
      <Route path="/admin/onboarding" element={<AdminOnly><AdminOnboardingPage /></AdminOnly>} />
      <Route path="/admin/wasted-warm-leads" element={<AdminOnly><AdminWastedWarmLeadsPage /></AdminOnly>} />
      <Route path="/admin/llm-routing" element={<AdminOnly><AdminLlmRoutingPage /></AdminOnly>} />
      <Route path="/admin/demo-leads" element={<AdminOnly><AdminDemoLeadsPage /></AdminOnly>} />
      <Route path="/admin/queue-watchdog" element={<AdminOnly><AdminQueueWatchdogPage /></AdminOnly>} />
      <Route path="/admin/vcon" element={<AdminOnly><AdminVconPage /></AdminOnly>} />
      <Route path="/admin/weekly-reports" element={<AdminOnly><AdminWeeklyReportsPage /></AdminOnly>} />
      <Route path="/admin/cost-statements" element={<AdminOnly><AdminCostStatementsPage /></AdminOnly>} />
      <Route path="/admin/rebaseline-review" element={<AdminOnly><AdminRebaselineReviewPage /></AdminOnly>} />
      <Route path="/admin/yvf" element={<AdminOnly><AdminYvfCalibrationPage /></AdminOnly>} />
      <Route path="/admin/compliance-scorecard" element={<AdminOnly><AdminComplianceScorecardPage /></AdminOnly>} />
      <Route path="/admin/llm-provider" element={<AdminOnly><AdminLlmProviderPage /></AdminOnly>} />
      <Route path="/admin/impersonation-log" element={<AdminOnly><ImpersonationLogPage /></AdminOnly>} />

      {/* Demo / standard app (preserved) */}
      <Route path="/dashboard" element={<Protected><Layout><Dashboard /></Layout></Protected>} />
      <Route path="/calls" element={<Protected><Layout><CallsListPage /></Layout></Protected>} />
      <Route path="/achievements" element={<Protected><Layout><AchievementsPage /></Layout></Protected>} />
      <Route path="/calls/:callId" element={<Protected><Layout><CallDetailPage /></Layout></Protected>} />
      <Route path="/calls/:callId/voicefabric" element={<AdminOnly><CallVoiceFabricPage /></AdminOnly>} />
      <Route path="/knowledge-bases" element={<Protected><Layout><KnowledgeBasesPage /></Layout></Protected>} />
      {/* Global settings are master-admin only — tenant users don't need to
          see whitelabel branding / Yabbit UCaaS credentials / global sync
          controls. Deep-linking is blocked at the route level too. */}
      <Route path="/settings" element={<AdminOnly><Layout><SettingsPage /></Layout></AdminOnly>} />

      {/* Domain users: goes to standard app (scoped by domain_id on backend) */}
      <Route path="/my-domain" element={<Protected><Layout><Dashboard /></Layout></Protected>} />
      <Route path="/my-domain/calls" element={<Protected><Layout><CallsListPage /></Layout></Protected>} />
      <Route path="/my-domain/calls/:callId" element={<Protected><Layout><CallDetailPage /></Layout></Protected>} />
      <Route path="/my-domain/knowledge-bases" element={<Protected><Layout><KnowledgeBasesPage /></Layout></Protected>} />

      {/* Root redirect */}
      <Route path="/" element={user ? <Navigate to={defaultRedirect()} replace /> : <HomePage />} />
      <Route path="/portal" element={<Navigate to={user ? defaultRedirect() : '/login'} replace />} />
      <Route path="/my/performance" element={<Protected><Layout><MyPerformancePage /></Layout></Protected>} />
      <Route path="/supervisor/today" element={<Protected><Layout><SupervisorTodayPage /></Layout></Protected>} />
      <Route path="/manager/ops" element={<Protected><Layout><ManagerOpsDashboardPage /></Layout></Protected>} />
      <Route path="/team/users" element={<Protected><Layout><TeamUsersPage /></Layout></Protected>} />
      <Route path="/sms-inbox" element={<Protected><Layout><SmsInboxPage /></Layout></Protected>} />
      <Route path="/gymsales-sync" element={<Protected><Layout><GymSalesSyncPage /></Layout></Protected>} />
      <Route path="/disc-insights" element={<Protected><Layout><DiscInsightsPage /></Layout></Protected>} />
      <Route path="/command-center" element={<Protected><Layout><CommandCenterPage /></Layout></Protected>} />
      <Route path="/manager/agent/:agentId" element={<Protected><Layout><AgentProfilePage /></Layout></Protected>} />
      <Route path="/livechat" element={<Protected><Layout><LiveChatPage /></Layout></Protected>} />
      <Route path="/club-summary" element={<Protected><Layout><ClubSummaryPage /></Layout></Protected>} />
      <Route path="/help" element={<Protected><Layout><HelpHubPage /></Layout></Protected>} />
      <Route path="/help/:sectionId" element={<Protected><Layout><HelpHubPage /></Layout></Protected>} />
      <Route path="/training" element={<Protected><Layout><TrainingHubPage /></Layout></Protected>} />
      <Route path="/training/kb/:kbId" element={<Protected><Layout><TrainingHubPage /></Layout></Protected>} />
      <Route path="/training/:itemId" element={<Protected><Layout><TrainingHubPage /></Layout></Protected>} />
      <Route path="/admin/kb-site-configs" element={<Protected><AdminKBSiteConfigPage /></Protected>} />
      <Route path="/admin/site-scope" element={<Protected><AdminSiteScopePage /></Protected>} />
      <Route path="/admin/scope-leaks" element={<Protected><AdminScopeLeaksPage /></Protected>} />
      <Route path="/admin/email" element={<Protected><AdminEmailStatePage /></Protected>} />
      <Route path="/manager/coaching-records" element={<Protected><Layout><ManagerCoachingRecordsPage /></Layout></Protected>} />
      <Route path="/integrations" element={<Protected><TenantIntegrationsPage /></Protected>} />
      <Route path="/my-domain/integrations" element={<Protected><TenantIntegrationsPage /></Protected>} />
      <Route path="/branding" element={<Protected><TenantBrandingPage /></Protected>} />
      <Route path="/my-domain/branding" element={<Protected><TenantBrandingPage /></Protected>} />
      <Route path="*" element={<Navigate to={defaultRedirect()} replace />} />
    </Routes>
    </PhoneProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <BrowserRouter>
          <div className="App">
            <ImpersonationBanner />
            <LlmBudgetBanner />
            <AppRoutes />
            <Toaster position="top-right" />
          </div>
        </BrowserRouter>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
